import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  Settings, 
  BarChart3, 
  LogOut,
  Plus,
  Search,
  Filter,
  MoreVertical,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Activity,
  Zap,
  Edit,
  Key,
  Trash2,
  Shield,
  Layers,
  Cpu,
  Book,
  Code,
  FileText
} from 'lucide-react';
import CompanyConfiguration from './CompanyConfiguration';
import AgentManagement from './AgentManagement';
import AIProviderConfig from './AIProviderConfig';
import ThemeToggle from './ThemeToggle';
import TenantConfiguration from './TenantConfiguration';
import TenantConfigurationSimple from './TenantConfigurationSimple';
import IndustryGatewayConfig from './IndustryGatewayConfig';
import IndustryTaxonomyManager from './IndustryTaxonomyManager';
import AgentTypeConfig from './AgentTypeConfig';
import InternalPromptsConfig from './InternalPromptsConfig';
import IndustryBenchmarkManager from './IndustryBenchmarkManager';
import ExternalAPIKeysManager from './ExternalAPIKeysManager';
import UserManualModule from './UserManualModule';
import AIGovernanceDashboard from './AIGovernanceDashboard';
import GovernanceV2Panel from './governance_v2/GovernanceV2Panel';
import IADLibraryManager from './iad/IADLibraryManager';
import { HistoricalBenchmark } from './historical/HistoricalBenchmark';
import { ShieldCheck, BookOpenCheck, Globe2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [govV2CompanyId, setGovV2CompanyId] = useState('');
  const [newCompany, setNewCompany] = useState({ 
    name: '', 
    domain: '',
    razon_social: '',
    nombre_comercial: '',
    ceo_director: '',
    email_principal: '',
    telefono: '',
    industria: '',
    pais: '',
    ciudad: '',
    empleados: ''
  });
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showAgentManagement, setShowAgentManagement] = useState(false);
  const [showAIProviderConfig, setShowAIProviderConfig] = useState(false);
  const [showTenantConfig, setShowTenantConfig] = useState(false);
  const [tenantConfigCompany, setTenantConfigCompany] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [governanceConfig, setGovernanceConfig] = useState({
    master_prompt: '',
    enabled_modules: [],
    default_model: 'gpt-4.1',
    html_render_prompt: ''
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    keepPreviousData: true
  });

  const { data: modulesRaw = [], isLoading: modulesLoading, error: modulesError } = useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      const response = await axios.get(`${API}/modules`);
      return response.data;
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    keepPreviousData: true, // Keep showing old data while fetching
    onError: (error) => {
      console.error('Error loading modules:', error);
      toast.error(`Error al cargar módulos: ${error.response?.data?.detail || error.message}`);
    }
  });

  // Sort modules in specific order: Revenue, Supply Chain, Logistics, Operations, Finance, HR
  const modules = React.useMemo(() => {
    const moduleOrder = {
      'revenue': 1,
      'supply_chain': 2,
      'logistica': 3,
      'logistics': 3,
      'operaciones': 4,
      'operations': 4,
      'finanzas': 5,
      'finance': 5,
      'rrhh': 6,
      'human_resources': 6,
    };
    
    return [...modulesRaw].sort((a, b) => {
      const orderA = moduleOrder[a.type?.toLowerCase()] || 999;
      const orderB = moduleOrder[b.type?.toLowerCase()] || 999;
      return orderA - orderB;
    });
  }, [modulesRaw]);

  const { data: governanceAgentConfig } = useQuery({
    queryKey: ['governance-agent-config'],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance-agent/config`);
      return response.data;
    }
  });

  // Update governance config when data is loaded
  React.useEffect(() => {
    if (governanceAgentConfig) {
      setGovernanceConfig(governanceAgentConfig);
    }
  }, [governanceAgentConfig]);

  // Mutations
  const createCompanyMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/companies`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
      setShowCreateCompany(false);
      setNewCompany({ 
        name: '', 
        domain: '',
        razon_social: '',
        nombre_comercial: '',
        ceo_director: '',
        email_principal: '',
        telefono: '',
        industria: '',
        pais: '',
        ciudad: '',
        empleados: ''
      });
      toast.success('Empresa creada exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al crear empresa');
    }
  });

  const deleteCompanyMutation = useMutation({
    mutationFn: async (companyId) => {
      const response = await axios.delete(`${API}/companies/${companyId}`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['companies']);
      setShowDeleteDialog(false);
      setCompanyToDelete(null);
      toast.success(data.message || 'Empresa eliminada exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar empresa');
    }
  });

  const toggleGovernanceAgentMutation = useMutation({
    mutationFn: async ({ companyId, enabled }) => {
      const response = await axios.patch(`${API}/companies/${companyId}/governance-agent?enabled=${enabled}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
      toast.success('Super Agente de Gobernanza actualizado');
    },
    onError: (error) => {
      console.error('Error toggling governance agent:', error);
      let errorMessage = 'Error al actualizar Super Agente';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle validation errors (array of objects)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Handle string errors
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Handle object errors
        else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  });

  const updateGovernanceConfigMutation = useMutation({
    mutationFn: async (config) => {
      const response = await axios.post(`${API}/governance-agent/config`, config);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['governance-agent-config']);
      toast.success('Configuración del Super Agente actualizada');
    },
    onError: (error) => {
      console.error('Error updating governance config:', error);
      let errorMessage = 'Error al actualizar configuración';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle validation errors (array of objects)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Handle string errors
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Handle object errors
        else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  });

  const handleCreateCompany = () => {
    if (!newCompany.name || !newCompany.domain) {
      toast.error('Complete todos los campos');
      return;
    }
    createCompanyMutation.mutate(newCompany);
  };

  const handleConfigureCompany = (company) => {
    setSelectedCompany(company);
  };

  const handleDeleteCompany = (company) => {
    setCompanyToDelete(company);
    setShowDeleteDialog(true);
  };

  const handleOpenTenantConfig = (company) => {
    console.log('[handleOpenTenantConfig] Opening for company:', company);
    setTenantConfigCompany(company);
    setShowTenantConfig(true);
  };

  const confirmDeleteCompany = () => {
    if (companyToDelete) {
      deleteCompanyMutation.mutate(companyToDelete.id);
    }
  };

  const handleToggleGovernanceAgent = (company, checked) => {
    toggleGovernanceAgentMutation.mutate({
      companyId: company.id,
      enabled: checked
    });
  };

  const handleSaveGovernanceConfig = () => {
    updateGovernanceConfigMutation.mutate(governanceConfig);
  };

  // If showing AI provider config, show the AI provider config component
  if (showAIProviderConfig) {
    return (
      <AIProviderConfig 
        onBack={() => setShowAIProviderConfig(false)}
      />
    );
  }

  // If showing agent management, show the agent management component
  if (showAgentManagement) {
    return (
      <AgentManagement 
        onBack={() => setShowAgentManagement(false)}
      />
    );
  }

  // If configuring a company, show the configuration component
  if (selectedCompany) {
    return (
      <CompanyConfiguration 
        company={selectedCompany}
        onBack={() => setSelectedCompany(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Sidebar */}
      <div className="w-64 sidebar border-r border-blue-500/20">
        <div className="sidebar-content">
          <div className="p-6">
            <h1 className="text-xl font-bold vector-logo mb-6">Vector Solutions</h1>
            
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'overview' ? 'active' : 'text-slate-300'
                }`}
                data-testid="overview-tab"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Overview</span>
              </button>
              
              <button
                onClick={() => setActiveTab('companies')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'companies' ? 'active' : 'text-slate-300'
                }`}
                data-testid="companies-tab"
              >
                <Building2 className="w-4 h-4" />
                <span>Empresas</span>
              </button>
              
              <button
                onClick={() => setActiveTab('governance-agent')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'governance-agent' ? 'active' : 'text-slate-300'
                }`}
                data-testid="governance-agent-tab"
              >
                <Shield className="w-4 h-4" />
                <span>Super Agente</span>
              </button>

              <button
                onClick={() => setActiveTab('industry-gateway')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'industry-gateway' ? 'active' : 'text-slate-300'
                }`}
              >
                <Layers className="w-4 h-4" />
                <span>Industry Gateway</span>
              </button>

              <button
                onClick={() => setActiveTab('agent-type-config')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'agent-type-config' ? 'active' : 'text-slate-300'
                }`}
              >
                <Cpu className="w-4 h-4" />
                <span>Config. Agentes</span>
              </button>

              <button
                onClick={() => setActiveTab('internal-prompts')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'internal-prompts' ? 'active' : 'text-slate-300'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Prompts Internos</span>
              </button>

              <button
                onClick={() => setActiveTab('industry-benchmarks')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'industry-benchmarks' ? 'active' : 'text-slate-300'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Benchmarks</span>
              </button>

              <button
                onClick={() => setActiveTab('governance-v2')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'governance-v2' ? 'active' : 'text-slate-300'
                }`}
                data-testid="governance-v2-tab"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Decisiones Gobernadas</span>
              </button>

              <button
                onClick={() => setActiveTab('historical-benchmark')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'historical-benchmark' ? 'active' : 'text-slate-300'
                }`}
                data-testid="historical-benchmark-tab"
              >
                <Globe2 className="w-4 h-4 text-violet-400" />
                <span>Benchmark histórico</span>
              </button>

              <button
                onClick={() => setActiveTab('iad-library')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'iad-library' ? 'active' : 'text-slate-300'
                }`}
                data-testid="iad-library-tab"
              >
                <BookOpenCheck className="w-4 h-4 text-amber-400" />
                <span>Biblioteca IAD</span>
              </button>

              <button
                onClick={() => setActiveTab('ai-governance')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'ai-governance' ? 'active' : 'text-slate-300'
                }`}
                data-testid="ai-governance-tab"
              >
                <Shield className="w-4 h-4 text-purple-400" />
                <span>Gobernanza IA</span>
              </button>
              
              <button
                onClick={() => setActiveTab('modules')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'modules' ? 'active' : 'text-slate-300'
                }`}
                data-testid="modules-tab"
              >
                <Zap className="w-4 h-4" />
                <span>Módulos</span>
              </button>
              
              <button
                onClick={() => setShowAgentManagement(true)}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item text-slate-300"
                data-testid="agent-management-tab"
              >
                <Activity className="w-4 h-4" />
                <span>Gestión de Agentes</span>
              </button>
              
              <button
                onClick={() => setShowAIProviderConfig(true)}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item text-slate-300"
                data-testid="ai-config-tab"
              >
                <Key className="w-4 h-4" />
                <span>API Keys IA</span>
              </button>

              <button
                onClick={() => setActiveTab('external-api-keys')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'external-api-keys' ? 'active' : 'text-slate-300'
                }`}
                data-testid="external-api-keys-tab"
              >
                <Shield className="w-4 h-4" />
                <span>API Externa</span>
              </button>
              
              <button
                onClick={() => setActiveTab('user-manual')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'user-manual' ? 'active' : 'text-slate-300'
                }`}
                data-testid="user-manual-tab"
              >
                <Book className="w-4 h-4" />
                <span>Manual de Usuario</span>
              </button>
              
              <button
                onClick={() => setActiveTab('settings')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm sidebar-item ${
                  activeTab === 'settings' ? 'active' : 'text-slate-300'
                }`}
                data-testid="settings-tab"
              >
                <Settings className="w-4 h-4" />
                <span>Configuración</span>
              </button>
            </nav>
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-white">
                {user?.full_name?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          
          <div className="mb-3">
            <ThemeToggle />
          </div>
          
          <Button
            onClick={logout}
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Panel de Super Admin</h1>
            <p className="text-slate-400">Gestione empresas, módulos y configuración global</p>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="glass-dark border-blue-500/20 dashboard-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Total Empresas</p>
                        <p className="text-2xl font-bold text-white">{companies.length}</p>
                      </div>
                      <Building2 className="w-8 h-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-dark border-green-500/20 dashboard-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Módulos Activos</p>
                        <p className="text-2xl font-bold text-white">{modules.length}</p>
                      </div>
                      <Zap className="w-8 h-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-dark border-purple-500/20 dashboard-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Agentes Revenue</p>
                        <p className="text-2xl font-bold text-white">15</p>
                      </div>
                      <Activity className="w-8 h-8 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-dark border-cyan-500/20 dashboard-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Sistema Status</p>
                        <p className="text-2xl font-bold text-green-400">Activo</p>
                      </div>
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="glass-dark border-blue-500/20">
                  <CardHeader>
                    <CardTitle className="text-white">Empresas Recientes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {companies.slice(0, 5).map((company) => (
                        <div key={company.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                          <div>
                            <p className="font-medium text-white">{company.name}</p>
                            <p className="text-sm text-slate-400">{company.domain}</p>
                          </div>
                          <Badge className={company.is_active ? 'badge-success' : 'badge-error'}>
                            {company.is_active ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-dark border-blue-500/20">
                  <CardHeader>
                    <CardTitle className="text-white">Módulos Disponibles</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {modules.map((module) => (
                        <div key={module.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                          <div>
                            <p className="font-medium text-white">{module.name}</p>
                            <p className="text-sm text-slate-400">{module.description}</p>
                          </div>
                          <Badge className="badge-info">{module.default_model}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Companies Tab */}
          {activeTab === 'companies' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white">Gestión de Empresas</h2>
                  <p className="text-slate-400">Administre las empresas cliente de la plataforma</p>
                </div>
                
                <Dialog open={showCreateCompany} onOpenChange={setShowCreateCompany}>
                  <DialogTrigger asChild>
                    <Button className="btn-gradient" data-testid="create-company-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Empresa
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-dark border-blue-500/20 text-white max-w-4xl">
                    <DialogHeader>
                      <DialogTitle className="text-xl">Crear Nueva Empresa - Perfil Completo</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                      {/* Información Básica */}
                      <div className="border-b border-slate-700 pb-4">
                        <h3 className="text-lg font-semibold text-white mb-3">Información Básica</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Nombre de la Empresa *
                            </label>
                            <Input
                              value={newCompany.name}
                              onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                              className="form-input"
                              placeholder="Vector Solutions"
                              data-testid="company-name-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Dominio *
                            </label>
                            <Input
                              value={newCompany.domain}
                              onChange={(e) => setNewCompany({ ...newCompany, domain: e.target.value })}
                              className="form-input"
                              placeholder="vectorsolutions.com"
                              data-testid="company-domain-input"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Información Legal */}
                      <div className="border-b border-slate-700 pb-4">
                        <h3 className="text-lg font-semibold text-white mb-3">Información Legal</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Razón Social
                            </label>
                            <Input
                              value={newCompany.razon_social}
                              onChange={(e) => setNewCompany({ ...newCompany, razon_social: e.target.value })}
                              className="form-input"
                              placeholder="Vector Solutions S.A."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Nombre Comercial
                            </label>
                            <Input
                              value={newCompany.nombre_comercial}
                              onChange={(e) => setNewCompany({ ...newCompany, nombre_comercial: e.target.value })}
                              className="form-input"
                              placeholder="Vector Solutions"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Información de Contacto */}
                      <div className="border-b border-slate-700 pb-4">
                        <h3 className="text-lg font-semibold text-white mb-3">Información de Contacto</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              CEO / Director
                            </label>
                            <Input
                              value={newCompany.ceo_director}
                              onChange={(e) => setNewCompany({ ...newCompany, ceo_director: e.target.value })}
                              className="form-input"
                              placeholder="Juan Pérez"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Email Principal
                            </label>
                            <Input
                              type="email"
                              value={newCompany.email_principal}
                              onChange={(e) => setNewCompany({ ...newCompany, email_principal: e.target.value })}
                              className="form-input"
                              placeholder="contacto@vectorsolutions.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Teléfono
                            </label>
                            <Input
                              value={newCompany.telefono}
                              onChange={(e) => setNewCompany({ ...newCompany, telefono: e.target.value })}
                              className="form-input"
                              placeholder="+502 1234-5678"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Información Empresarial */}
                      <div className="pb-4">
                        <h3 className="text-lg font-semibold text-white mb-3">Información Empresarial</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Industria
                            </label>
                            <Input
                              value={newCompany.industria}
                              onChange={(e) => setNewCompany({ ...newCompany, industria: e.target.value })}
                              className="form-input"
                              placeholder="Tecnología, Retail, Manufactura..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Número de Empleados
                            </label>
                            <Input
                              type="number"
                              value={newCompany.empleados}
                              onChange={(e) => setNewCompany({ ...newCompany, empleados: e.target.value })}
                              className="form-input"
                              placeholder="50"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              País
                            </label>
                            <Input
                              value={newCompany.pais}
                              onChange={(e) => setNewCompany({ ...newCompany, pais: e.target.value })}
                              className="form-input"
                              placeholder="Guatemala"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Ciudad
                            </label>
                            <Input
                              value={newCompany.ciudad}
                              onChange={(e) => setNewCompany({ ...newCompany, ciudad: e.target.value })}
                              className="form-input"
                              placeholder="Guatemala City"
                            />
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleCreateCompany}
                        className="w-full btn-gradient"
                        disabled={createCompanyMutation.isPending}
                        data-testid="confirm-create-company-btn"
                      >
                        {createCompanyMutation.isPending ? 'Creando...' : 'Crear Empresa'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <Card className="glass-dark border-blue-500/20">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300">Empresa</TableHead>
                        <TableHead className="text-slate-300">Dominio</TableHead>
                        <TableHead className="text-slate-300">Estado</TableHead>
                        <TableHead className="text-slate-300">Super Agente</TableHead>
                        <TableHead className="text-slate-300">Fecha Creación</TableHead>
                        <TableHead className="text-slate-300">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companies.map((company) => (
                        <TableRow key={company.id} className="border-slate-700 hover:bg-slate-800/50">
                          <TableCell className="font-medium text-white">{company.name}</TableCell>
                          <TableCell className="text-slate-300">{company.domain}</TableCell>
                          <TableCell>
                            <Badge className={company.is_active ? 'badge-success' : 'badge-error'}>
                              {company.is_active ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch 
                                checked={company.governance_agent_enabled || false}
                                onCheckedChange={(checked) => handleToggleGovernanceAgent(company, checked)}
                              />
                              <span className="text-xs text-slate-400">
                                {company.governance_agent_enabled ? 'Activo' : 'Inactivo'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {new Date(company.created_at).toLocaleDateString('es-ES')}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleConfigureCompany(company)}
                                className="text-blue-400 hover:text-blue-300"
                                data-testid={`configure-company-${company.id}`}
                                title="Editar empresa"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="default"
                                size="sm" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('[Settings Button Click] Event:', e);
                                  console.log('[Settings Button Click] Company:', company);
                                  handleOpenTenantConfig(company);
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                title="Configuración AWS & LLM"
                              >
                                <Settings className="w-4 h-4 mr-1" />
                                Config
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteCompany(company)}
                                className="text-red-400 hover:text-red-300"
                                data-testid={`delete-company-${company.id}`}
                                title="Eliminar empresa"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Super Governance Agent Tab */}
          {activeTab === 'governance-agent' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Super Agente de Gobernanza</h2>
                <p className="text-slate-400">Configure el agente integrador para análisis integrales a nivel directivo</p>
              </div>

              <Card className="glass-dark border-blue-500/20">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-blue-400" />
                    <CardTitle className="text-white">Configuración Global</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Prompt Maestro del Super Agente
                    </label>
                    <Textarea
                      value={governanceConfig.master_prompt}
                      onChange={(e) => setGovernanceConfig({ ...governanceConfig, master_prompt: e.target.value })}
                      placeholder="Describe el comportamiento y objetivos del super agente..."
                      className="form-input min-h-[200px]"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Este agente tendrá acceso a información de todos los módulos y agentes habilitados para cada empresa
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Módulos a Integrar
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {modules.map((module) => (
                        <div key={module.id} className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/50">
                          <Switch
                            checked={governanceConfig.enabled_modules?.includes(module.type) || false}
                            onCheckedChange={(checked) => {
                              const newModules = checked
                                ? [...(governanceConfig.enabled_modules || []), module.type]
                                : governanceConfig.enabled_modules.filter(m => m !== module.type);
                              setGovernanceConfig({ ...governanceConfig, enabled_modules: newModules });
                            }}
                          />
                          <span className="text-sm text-slate-300">{module.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Modelo por Defecto
                    </label>
                    <Select
                      value={governanceConfig.default_model}
                      onValueChange={(value) => setGovernanceConfig({ ...governanceConfig, default_model: value })}
                    >
                      <SelectTrigger className="form-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpt-5">GPT-5 (Más reciente)</SelectItem>
                        <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                        <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                        <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
                        <SelectItem value="claude-opus-4.1">Claude Opus 4.1 (Máxima capacidad)</SelectItem>
                        <SelectItem value="claude-sonnet-4.5">Claude Sonnet 4.5 (Balanceado)</SelectItem>
                        <SelectItem value="claude-haiku-4.5">Claude Haiku 4.5 (Rápido)</SelectItem>
                        <SelectItem value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</SelectItem>
                        <SelectItem value="claude-3-haiku-20240307">Claude 3 Haiku</SelectItem>
                        <SelectItem value="claude-3-opus-20240229">Claude 3 Opus</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* HTML Render Prompt - Custom HTML Instructions */}
                  <div className="border-t border-slate-700 pt-6 mt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Code className="w-5 h-5 text-purple-400" />
                      <h3 className="text-lg font-semibold text-white">Instrucciones de Renderizado HTML</h3>
                    </div>
                    <p className="text-sm text-slate-400 mb-4">
                      Define cómo la IA debe generar el formato HTML de las rutas estratégicas. 
                      Si se deja vacío, se usará el formato por defecto estilo McKinsey/BCG.
                    </p>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Prompt de Renderizado HTML (Opcional)
                    </label>
                    <textarea
                      className="w-full min-h-[300px] p-4 rounded-lg border border-slate-600 bg-slate-800 text-white font-mono text-sm resize-y focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                      placeholder={`Ejemplo de instrucciones de renderizado:

FORMATO DE RESPUESTA HTML:
- Usa estilos CSS inline en todos los elementos
- Colores principales: #1e3a5f (azul oscuro), #10b981 (verde), #ef4444 (rojo)
- Tipografía: font-family: 'Inter', sans-serif

ESTRUCTURA DEL REPORTE:
1. Header con logo y título
2. Resumen ejecutivo en cards
3. Métricas principales con indicadores de estado
4. Tabla de análisis detallado
5. Sección de recomendaciones con prioridad
6. Gráficas JSON para visualización

ESTILOS DE ESTADO:
- Crítico: fondo #fef2f2, borde #ef4444
- Atención: fondo #fffbeb, borde #f59e0b  
- Saludable: fondo #f0fdf4, borde #10b981

IMPORTANTE: Todo CSS debe ser inline. No usar clases CSS externas.`}
                      value={governanceConfig.html_render_prompt || ''}
                      onChange={(e) => setGovernanceConfig({ ...governanceConfig, html_render_prompt: e.target.value })}
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      💡 Tip: Incluye ejemplos de HTML, paleta de colores, estructura de componentes y reglas de formato.
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveGovernanceConfig}
                      className="btn-gradient"
                      disabled={updateGovernanceConfigMutation.isPending}
                    >
                      {updateGovernanceConfigMutation.isPending ? 'Guardando...' : 'Guardar Configuración'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-dark border-blue-500/20">
                <CardHeader>
                  <CardTitle className="text-white">Empresas con Super Agente Activo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {companies.filter(c => c.governance_agent_enabled).length === 0 ? (
                      <p className="text-slate-400 text-center py-4">
                        Ninguna empresa tiene el Super Agente habilitado actualmente
                      </p>
                    ) : (
                      companies.filter(c => c.governance_agent_enabled).map((company) => (
                        <div key={company.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                          <div>
                            <p className="font-medium text-white">{company.name}</p>
                            <p className="text-sm text-slate-400">{company.domain}</p>
                          </div>
                          <Badge className="badge-success">Activo</Badge>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Modules Tab */}


          {/* Industry Gateway Tab */}
          {activeTab === 'industry-gateway' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Industry Gateway Agent (GOV-ENT-000)</h2>
                <p className="text-slate-400">
                  Agente obligatorio que clasifica automáticamente cada tenant por industria
                </p>
              </div>

              {/* Configuration Section */}
              <IndustryGatewayConfig />

              {/* Taxonomy Section */}
              <div className="mt-12">
                <IndustryTaxonomyManager />
              </div>
            </div>
          )}

          {/* Agent Type Configuration Tab */}
          {activeTab === 'agent-type-config' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Configuración de Agentes por Tipo</h2>
                <p className="text-slate-400">
                  Define modelo, tokens, temperatura e instrucciones para cada tipo de agente (Funcional, Premium, C-Level, Conversación)
                </p>
              </div>
              <AgentTypeConfig />
            </div>
          )}

          {/* Internal Prompts Tab */}
          {activeTab === 'internal-prompts' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Prompts Internos de Agentes</h2>
                <p className="text-slate-400">
                  Edita los prompts de sistema que definen el comportamiento de cada nivel de agente (Nivel 1, 2, 3)
                </p>
              </div>
              <InternalPromptsConfig />
            </div>
          )}

          {/* Industry Benchmarks Tab */}
          {activeTab === 'industry-benchmarks' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Benchmarks de Industria</h2>
                <p className="text-slate-400">
                  Configura métricas de referencia por industria para generar KPIs automáticos cuando los agentes se ejecuten
                </p>
              </div>
              <IndustryBenchmarkManager />
            </div>
          )}

          {/* AI Governance Tab */}
          {activeTab === 'ai-governance' && (
            <AIGovernanceDashboard />
          )}

          {activeTab === 'iad-library' && <IADLibraryManager />}
          {activeTab === 'historical-benchmark' && <HistoricalBenchmark />}

          {activeTab === 'governance-v2' && (
            <div>
              <div className="px-8 pt-8">
                <select
                  className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                  value={govV2CompanyId}
                  onChange={(e) => setGovV2CompanyId(e.target.value)}
                  data-testid="governance-v2-company-select"
                >
                  <option value="">Selecciona una empresa…</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <GovernanceV2Panel key={govV2CompanyId} companyId={govV2CompanyId} user={user} />
            </div>
          )}

          {activeTab === 'modules' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Módulos del Sistema</h2>
                <p className="text-slate-400">Configure los módulos de inteligencia disponibles</p>
              </div>

              {modulesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-400">Cargando módulos...</p>
                  </div>
                </div>
              ) : modulesError ? (
                <Card className="glass-dark border-red-500/20">
                  <CardContent className="p-6">
                    <div className="text-center">
                      <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                      <p className="text-red-400 font-semibold mb-2">Error al cargar módulos</p>
                      <p className="text-slate-400 text-sm mb-4">
                        {modulesError.response?.data?.detail || modulesError.message || 'Error desconocido'}
                      </p>
                      <p className="text-slate-500 text-xs">
                        Verifica la consola del navegador para más detalles
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : modules.length === 0 ? (
                <Card className="glass-dark border-blue-500/20">
                  <CardContent className="p-6">
                    <div className="text-center text-slate-400">
                      <p>No hay módulos disponibles</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {modules.map((module) => (
                    <Card key={module.id} className="glass-dark border-blue-500/20 card-hover">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white">{module.name}</CardTitle>
                          <Badge className="badge-success">Activo</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-300 text-sm mb-4">{module.description}</p>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Modelo por Defecto:</span>
                            <span className="text-blue-400">{module.default_model}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Tipo:</span>
                            <span className="text-slate-300 capitalize">{module.type.replace('_', ' ')}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* External API Keys Tab */}
          {activeTab === 'external-api-keys' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">API Keys para Integraciones Externas</h2>
                <p className="text-slate-400">
                  Gestiona las API Keys para que Vector Bridge y otros sistemas externos puedan conectarse
                </p>
              </div>
              <ExternalAPIKeysManager />
            </div>
          )}

          {/* User Manual Tab */}
          {activeTab === 'user-manual' && (
            <UserManualModule userRole="super_admin" />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Configuración Global</h2>
                <p className="text-slate-400">Configure parámetros del sistema</p>
              </div>
              <Card className="glass-dark border-blue-500/20">
                <CardContent className="p-6">
                  <div className="text-center text-slate-400">
                    Panel de configuración global en desarrollo
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Delete Company Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="glass-dark border-red-500/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-300">
              ¿Está seguro que desea eliminar la empresa <strong>{companyToDelete?.name}</strong>?
            </p>
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <p className="text-sm text-red-300 mb-2 font-semibold">⚠️ Esta acción no se puede deshacer</p>
              <p className="text-sm text-slate-400">Se eliminarán permanentemente:</p>
              <ul className="text-sm text-slate-400 list-disc list-inside mt-2 space-y-1">
                <li>Todos los usuarios de la empresa</li>
                <li>Configuración de módulos</li>
                <li>Agentes activados</li>
                <li>Historial de ejecuciones</li>
              </ul>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setCompanyToDelete(null);
                }}
                variant="ghost"
                className="text-slate-300"
                disabled={deleteCompanyMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmDeleteCompany}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleteCompanyMutation.isPending}
                data-testid="confirm-delete-company"
              >
                {deleteCompanyMutation.isPending ? 'Eliminando...' : 'Eliminar Empresa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tenant Configuration Modal */}
      <Dialog open={showTenantConfig} onOpenChange={(open) => {
        setShowTenantConfig(open);
        if (!open) {
          setTenantConfigCompany(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 text-white">
          <DialogHeader>
            <DialogTitle>Configuración de Tenant</DialogTitle>
          </DialogHeader>
          {tenantConfigCompany ? (
            <TenantConfiguration company={tenantConfigCompany} />
          ) : (
            <div className="p-4 text-red-400">
              <p>No se pudo cargar la información de la empresa</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;